import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './DripSchedule.css';

const RELEASE_TYPES = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'days_after_enrollment', label: 'Days After Enrollment' },
  { value: 'fixed_date', label: 'Fixed Date' },
];

const DripSchedule = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [courseName, setCourseName] = useState('');
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showWeeklyModal, setShowWeeklyModal] = useState(false);
  const [weeklyDay, setWeeklyDay] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [courseRes, modulesRes] = await Promise.all([
          api.get(`/courses/${courseId}`),
          api.get(`/courses/${courseId}/modules`),
        ]);
        setCourseName(courseRes.data.title);
        setModules(
          modulesRes.data.map((m) => ({
            id: m.id,
            title: m.title,
            order_index: m.order_index,
            release_type: m.release_type || 'immediate',
            drip_days: m.drip_days ?? 0,
            release_date: m.release_date ? m.release_date.split('T')[0] : '',
            is_preview: m.is_preview || false,
            lesson_count: parseInt(m.lesson_count) || 0,
          }))
        );
      } catch (err) {
        setError('Failed to load course data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId]);

  const updateModule = (moduleId, field, value) => {
    setModules((prev) =>
      prev.map((m) => {
        if (m.id !== moduleId) return m;
        const updated = { ...m, [field]: value };
        if (field === 'release_type' && value === 'immediate') {
          updated.drip_days = 0;
          updated.release_date = '';
        }
        return updated;
      })
    );
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      for (const module of modules) {
        const payload = {
          release_type: module.release_type,
          drip_days: module.release_type === 'days_after_enrollment' ? parseInt(module.drip_days) || 0 : 0,
          release_date: module.release_type === 'fixed_date' ? module.release_date || null : null,
          is_preview: module.is_preview,
        };
        await api.put(`/courses/modules/${module.id}`, payload);
      }
      setSuccess('Schedule saved successfully.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleSetImmediate = async () => {
    if (!window.confirm('Set all modules to immediate release? This clears all drip settings.')) return;
    setBulkSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/scheduling/setup-immediate/${courseId}`);
      setModules((prev) =>
        prev.map((m) => ({ ...m, release_type: 'immediate', drip_days: 0, release_date: '' }))
      );
      setSuccess('All modules set to immediate release.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update schedule');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleSetWeekly = async () => {
    setBulkSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/scheduling/setup-weekly/${courseId}`, {
        release_day: weeklyDay,
        start_with_module: false,
      });
      // Reload modules to reflect new drip_days
      const modulesRes = await api.get(`/courses/${courseId}/modules`);
      setModules(
        modulesRes.data.map((m) => ({
          id: m.id,
          title: m.title,
          order_index: m.order_index,
          release_type: m.release_type === 'weekly' ? 'days_after_enrollment' : (m.release_type || 'immediate'),
          drip_days: m.drip_days ?? 0,
          release_date: m.release_date ? m.release_date.split('T')[0] : '',
          is_preview: m.is_preview || false,
          lesson_count: parseInt(m.lesson_count) || 0,
        }))
      );
      setSuccess('Weekly schedule configured.');
      setShowWeeklyModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to configure weekly schedule');
    } finally {
      setBulkSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page drip-schedule-page">
      <div className="container">
        {/* Hero */}
        <section className="drip-hero">
          <div className="hero-content">
            <p className="eyebrow">Content Release</p>
            <h1>Drip Schedule</h1>
            {courseName && <p>{courseName}</p>}
          </div>
          <div className="hero-actions">
            <button className="btn-secondary" onClick={() => navigate(-1)} disabled={saving}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || bulkSaving}>
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </section>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Bulk Actions */}
        <section className="drip-bulk-section">
          <h2>Quick Setup</h2>
          <p className="section-desc">Apply a schedule to all modules at once, then fine-tune below.</p>
          <div className="bulk-actions">
            <button
              className="bulk-btn immediate-btn"
              onClick={handleSetImmediate}
              disabled={bulkSaving || saving}
            >
              <span className="bulk-icon">⚡</span>
              <span className="bulk-label">Set All Immediate</span>
              <span className="bulk-hint">All content available on enrollment</span>
            </button>
            <button
              className="bulk-btn weekly-btn"
              onClick={() => setShowWeeklyModal(true)}
              disabled={bulkSaving || saving}
            >
              <span className="bulk-icon">📅</span>
              <span className="bulk-label">Configure Weekly Drip</span>
              <span className="bulk-hint">Release one module per week</span>
            </button>
          </div>
        </section>

        {/* Modules */}
        <section className="drip-modules-section">
          <div className="section-header">
            <h2>Modules ({modules.length})</h2>
            <span className="section-hint">Configure release settings per module</span>
          </div>

          {modules.length === 0 ? (
            <div className="empty-modules">
              <p>No modules found. Add modules to this course first.</p>
              <button className="btn-secondary" onClick={() => navigate(`/edit-course/${courseId}`)}>
                Go to Course Editor
              </button>
            </div>
          ) : (
            <div className="modules-list">
              {[...modules]
                .sort((a, b) => a.order_index - b.order_index)
                .map((module, index) => (
                  <div key={module.id} className="module-schedule-card">
                    <div className="module-card-header">
                      <div className="module-identity">
                        <span className="module-number">Module {index + 1}</span>
                        <span className="module-title">{module.title}</span>
                        {module.lesson_count > 0 && (
                          <span className="lesson-count">{module.lesson_count} lesson{module.lesson_count !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                      <div className="module-status">
                        {module.release_type === 'immediate' && (
                          <span className="status-pill status-immediate">Immediate</span>
                        )}
                        {module.release_type === 'days_after_enrollment' && (
                          <span className="status-pill status-drip">Day {module.drip_days || 0}</span>
                        )}
                        {module.release_type === 'fixed_date' && (
                          <span className="status-pill status-fixed">Fixed Date</span>
                        )}
                        {module.release_type === 'weekly' && (
                          <span className="status-pill status-weekly">Week {Math.floor((module.drip_days || 0) / 7)}</span>
                        )}
                        {module.is_preview && (
                          <span className="status-pill status-preview">Preview</span>
                        )}
                      </div>
                    </div>

                    <div className="module-card-body">
                      <div className="schedule-row">
                        <div className="form-group">
                          <label>Release Type</label>
                          <select
                            value={module.release_type === 'weekly' ? 'days_after_enrollment' : module.release_type}
                            onChange={(e) => updateModule(module.id, 'release_type', e.target.value)}
                          >
                            {RELEASE_TYPES.map((rt) => (
                              <option key={rt.value} value={rt.value}>{rt.label}</option>
                            ))}
                          </select>
                        </div>

                        {(module.release_type === 'days_after_enrollment' || module.release_type === 'weekly') && (
                          <div className="form-group">
                            <label>Days After Enrollment</label>
                            <input
                              type="number"
                              min="0"
                              max="365"
                              value={module.drip_days}
                              onChange={(e) => updateModule(module.id, 'drip_days', e.target.value)}
                            />
                            <span className="field-hint">
                              {module.drip_days === 0 || module.drip_days === '0'
                                ? 'Available immediately on enrollment'
                                : `Unlocks ${module.drip_days} day${parseInt(module.drip_days) !== 1 ? 's' : ''} after enrollment`}
                            </span>
                          </div>
                        )}

                        {module.release_type === 'fixed_date' && (
                          <div className="form-group">
                            <label>Release Date</label>
                            <input
                              type="date"
                              value={module.release_date}
                              onChange={(e) => updateModule(module.id, 'release_date', e.target.value)}
                            />
                            <span className="field-hint">
                              {module.release_date
                                ? `Unlocks on ${new Date(module.release_date + 'T00:00:00').toLocaleDateString()}`
                                : 'Select a date'}
                            </span>
                          </div>
                        )}
                      </div>

                      <label className="toggle-item preview-toggle">
                        <input
                          type="checkbox"
                          checked={module.is_preview}
                          onChange={(e) => updateModule(module.id, 'is_preview', e.target.checked)}
                        />
                        <span className="toggle-label">Free Preview</span>
                        <span className="toggle-hint">Visible to non-enrolled users</span>
                      </label>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </section>
      </div>

      {/* Weekly Modal */}
      {showWeeklyModal && (
        <div className="modal-overlay" onClick={() => setShowWeeklyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Configure Weekly Drip</h2>
              <button type="button" className="modal-close" onClick={() => setShowWeeklyModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Each module will unlock one week apart, starting with Module 1 at enrollment.</p>
              <div className="form-group">
                <label>Preferred Release Day</label>
                <select value={weeklyDay} onChange={(e) => setWeeklyDay(parseInt(e.target.value))}>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                  <option value={0}>Sunday</option>
                </select>
              </div>
              <div className="weekly-preview">
                {modules.slice(0, 4).map((m, i) => (
                  <div key={m.id} className="weekly-preview-row">
                    <span className="week-label">Week {i + 1}</span>
                    <span className="week-module">{m.title}</span>
                  </div>
                ))}
                {modules.length > 4 && (
                  <p className="weekly-more">+ {modules.length - 4} more modules</p>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowWeeklyModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSetWeekly} disabled={bulkSaving}>
                {bulkSaving ? 'Applying...' : 'Apply Weekly Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DripSchedule;
